import { atom } from 'jotai'
import { atomFamily } from 'jotai-family'

import type { ChatMessage } from '@/lib/types'
import { randomUUID } from '@/lib/utils'
import type { ResearchProposal } from '@/service/research.api'
import { markResearchThreadViewedAtom, researchJobsAtom } from '@/state/research'

export type ChatSendingState = 'ready' | 'generating' | 'error' | 'submitted'

export interface ResearchFollowupTarget {
  parentJobId: string
  mode: 'simple' | 'report'
}

// ─── Classify/confirm-card research flow (behind RESEARCH_CLASSIFY_ENABLED — see
// features/order/research/research-classify-flag.ts and the "composed-hugging-hammock" plan).
// This is the panel-scoped conversation state the classify flow needs BEFORE any ResearchJob
// exists — `serverThreadId` in particular is returned by classify itself, so it can't live on
// ResearchJob (nothing to poll yet), and must survive a CLARIFY round-trip with zero jobs
// involved.

/** A proposal/refusal/error card currently rendered in this panel, awaiting the user's tap.
 *  Exactly one per panel at a time — a new classify attempt only starts once this is cleared
 *  (the composer is disabled while it's 'pending'/'confirming', see assistant-runtime-provider). */
export interface PendingResearchAction {
  /** Local token — the ONLY thing the stale-response guard compares. Never compared against the
   *  server's own request_id (different namespace, no guaranteed equality — see `serverRequestId`). */
  attemptId: string
  /** classify's own request_id, kept for logging/tracing only. */
  serverRequestId: string
  /** The message this attempt classified — needed to retry after a classify error, or to
   *  resubmit with user_opt_in on "run anyway", without re-appending a user message. */
  originalMessage: string
  /** The assistant message this action's card lives in — confirm/cancel/retry replace THIS
   *  message's parts in place rather than appending a new one. */
  assistantMessageId: string
  status: 'pending' | 'confirming' | 'resolved'
  data:
    | {
        kind: 'proposal'
        route: 'FULL_RESEARCH' | 'FOLLOWUP_ON_EXISTING'
        proposal: ResearchProposal
      }
    | { kind: 'refusal'; userMessage: string }
    /** classify itself failed (network/parse error) — retryable, not a route. `userOptIn`
     *  carries through a retry so a failed "run anyway" attempt still retries AS a run-anyway,
     *  not a plain resend. */
    | { kind: 'error'; message: string; userOptIn: boolean }
}

export interface ResearchConversationState {
  /** The backend's own conversation identity for /research/classify — distinct from this
   *  panel's client-generated `threadId` used everywhere else in this file. */
  serverThreadId?: string
  /** false while this thread holds only part of its server conversation (reopened from the
   *  history list, or a run restored after a reload) — opening it then fills in the turns from
   *  GET /research/threads/{id}. Undefined for a conversation started in this session. */
  historyLoaded?: boolean
  classifying: boolean
  /** The most recent in-flight classify attempt's token — the stale-response guard compares an
   *  arriving response's own attempt token against this, NOT against the server's request_id
   *  (different namespace). Set the moment an attempt starts, independent of `pendingAction`
   *  (which doesn't exist yet while classifying). */
  classifyAttemptId?: string
  pendingAction?: PendingResearchAction
}

export interface ChatThreadMeta {
  threadId: string
  surfaceId: string
  cardId?: string
  pageId?: string
  source: 'widget' | 'sheet' | 'panel' | 'history'
  title: string
  createdAt: number
  updatedAt: number
}

const threadDefaultId = (surfaceId: string) => `thread:${surfaceId}`

export const activeThreadIdsAtom = atom<Record<string, string>>({})
export const activeThreadIdAtomFamily = atomFamily((surfaceId: string) =>
  atom(
    (get) => get(activeThreadIdsAtom)[surfaceId] ?? threadDefaultId(surfaceId),
    (get, set, threadId: string) => {
      const current = get(activeThreadIdsAtom)
      if (current[surfaceId] === threadId) return
      set(activeThreadIdsAtom, { ...current, [surfaceId]: threadId })
    }
  )
)

export const knownThreadIdsAtom = atom<string[]>([])
export const threadMetaAtomFamily = atomFamily((threadId: string) =>
  atom<ChatThreadMeta | null>(null)
)
export const chatIdAtomFamily = atomFamily((_threadId: string) => atom(''))
export const messagesAtomFamily = atomFamily((_threadId: string) => atom<ChatMessage[]>([]))
export const sendingStateAtomFamily = atomFamily((_threadId: string) =>
  atom<ChatSendingState>('ready')
)
export const welcomeFlowKeyAtomFamily = atomFamily((_threadId: string) => atom(0))
export const widgetExplainPendingAtomFamily = atomFamily((_threadId: string) => atom(false))

/** A prompt handed to the assistant from outside its composer (e.g. the Order Form's prompt bar).
 *  The thread's runtime provider sends it once mounted and clears it; `requestId` keeps a stale
 *  consumer from clearing a newer request. */
export interface AssistantPendingPrompt {
  requestId: string
  threadId: string
  text: string
}
export const assistantPendingPromptAtom = atom<AssistantPendingPrompt | null>(null)
export const researchFollowupTargetAtomFamily = atomFamily((_threadId: string) =>
  atom<ResearchFollowupTarget | null>(null)
)
export const researchConversationAtomFamily = atomFamily((_threadId: string) =>
  atom<ResearchConversationState>({ classifying: false })
)

/** serverThreadId → the local thread holding that conversation, for every thread that has one. */
export const serverThreadIndexAtom = atom((get) => {
  const index = new Map<string, string>()
  for (const threadId of get(knownThreadIdsAtom)) {
    const serverThreadId = get(researchConversationAtomFamily(threadId)).serverThreadId
    if (serverThreadId) index.set(serverThreadId, threadId)
  }
  return index
})

export const autoResumeAtom = atom(false)

export function createChatThreadId() {
  return `thread:${randomUUID()}`
}

export const registerChatThreadAtom = atom(null, (get, set, meta: ChatThreadMeta) => {
  if (!get(knownThreadIdsAtom).includes(meta.threadId)) {
    set(knownThreadIdsAtom, (ids) => [...ids, meta.threadId])
  }
  const current = get(threadMetaAtomFamily(meta.threadId))
  set(
    threadMetaAtomFamily(meta.threadId),
    current ? { ...meta, ...current, updatedAt: Math.max(current.updatedAt, meta.updatedAt) } : meta
  )
})

/** Resume on a surface: jump to the newest unseen research result, else stay on its thread. */
export const activateAskChatThreadAtom = atom(
  null,
  (get, set, { surfaceId }: { surfaceId: string }) => {
    const jobs = Object.values(get(researchJobsAtom))
    const unseenResearch = jobs
      .filter((job) => job.phase === 'DELIVERED' && !job.viewedAt)
      .sort((a, b) => (b.completedAt ?? b.updatedAt) - (a.completedAt ?? a.updatedAt))[0]

    if (unseenResearch) {
      set(activeThreadIdAtomFamily(surfaceId), unseenResearch.threadId)
      set(markResearchThreadViewedAtom, unseenResearch.threadId)
    }
  }
)

const THREAD_ATOM_FAMILIES = [
  threadMetaAtomFamily,
  chatIdAtomFamily,
  messagesAtomFamily,
  sendingStateAtomFamily,
  welcomeFlowKeyAtomFamily,
  widgetExplainPendingAtomFamily,
  researchFollowupTargetAtomFamily,
  researchConversationAtomFamily,
]

// atomFamily caches every threadId's atom forever, so without this each thread (and its whole
// message history) stays in memory for the session. Called after the reset writes above: those
// re-render anything still subscribed to the old atoms, which then picks up a fresh default atom.
function removeThreadFamilyEntries(threadId: string) {
  for (const family of THREAD_ATOM_FAMILIES) family.remove(threadId)
}

export const clearChatThreadsAtom = atom(null, (get, set) => {
  for (const threadId of get(knownThreadIdsAtom)) {
    set(chatIdAtomFamily(threadId), '')
    set(messagesAtomFamily(threadId), [])
    set(sendingStateAtomFamily(threadId), 'ready')
    set(welcomeFlowKeyAtomFamily(threadId), 0)
    set(widgetExplainPendingAtomFamily(threadId), false)
    set(researchFollowupTargetAtomFamily(threadId), null)
    set(researchConversationAtomFamily(threadId), { classifying: false })
    removeThreadFamilyEntries(threadId)
  }
  set(activeThreadIdsAtom, {})
  set(knownThreadIdsAtom, [])
})

/** The assistant's own surface: the docked right-hand panel and the /trade/assistant page show
 *  the same thread, so moving between them never loses the conversation. */
export const ASSISTANT_SURFACE_ID = 'panel:assistant'
/** Passed to `Chat` as its cardId; order cards resolve it to the live Order Form target. */
export const ASSISTANT_CARD_ID = 'assistant'
export const assistantPanelOpenAtom = atom(false)
/** Set while /trade/assistant is mounted — the page then hosts the surface and the panel hides. */
export const assistantPageMountedAtom = atom(false)
/**
 * Set by the floating panel's Expand just before it routes to /trade/assistant: that entry moves
 * the current conversation onto the page. Every other entry (sidebar, a direct load) opens the
 * page on a new chat. Read and cleared once by AssistantPage on mount.
 */
export const assistantPageCarryThreadAtom = atom(false)
